import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { BrandInfo, GeneratedPost, Workspace } from '@agency-smm/shared';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { apiClient } from '../services/apiClient';
import { PageSkeleton } from '../components/PageSkeleton';
import { GenerateOptionsModal } from '../components/GenerateOptionsModal';
import { buildChecklist } from './workspaceHub/completeness';
import {
  HubLayout, BrandHeader, BrandIdentity, BrandLogoTile, BrandLogoFallback,
  BrandTitleBlock, BrandName, BrandMetaRow, WebsiteLink, MetaDot,
  HeaderActions, ActionButton, HubGrid, Column, Card, CardHead, CardTitle,
  CardLink, Overview, EmptyHint, TagRow, Tag, ScoreRow, ScoreRing, ScoreValue,
  ScoreRingWrap, ScoreText, CheckList, CheckRow, CheckMark, CheckLabel,
  CheckAction, CheckGo, RequiredBadge, Alert, AlertIcon, AlertBody, AlertFix, PostsGrid, PostCell,
  PostThumb, PostTextFallback, PostNetwork, Swatches, Swatch, StatsRow, Stat,
  NotFoundBox,
} from './workspaceHub/styled';

const RECENT_POSTS_LIMIT = 12;

/* Gradiente determinístico por id — mismo workspace, mismo color siempre. */
const GRADIENTS = [
  'linear-gradient(135deg, #6366F1, #8B5CF6)',
  'linear-gradient(135deg, #EC4899, #F43F5E)',
  'linear-gradient(135deg, #06B6D4, #3B82F6)',
  'linear-gradient(135deg, #10B981, #14B8A6)',
  'linear-gradient(135deg, #F59E0B, #EF4444)',
  'linear-gradient(135deg, #8B5CF6, #EC4899)',
];
const gradientFor = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
};

/* Iconos SVG inline — nada de emoji: no heredan el color del tema y se dibujan
   distinto en cada sistema operativo. */
const IconSpark = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 1.5l1.6 4.1 4.4.6-3.2 3 .8 4.3L8 11.5l-3.6 2 .8-4.3-3.2-3 4.4-.6L8 1.5z"
      fill="currentColor" />
  </svg>
);
const IconCheck = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconDash = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M3 6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const IconWarn = () => (
  <svg width="17" height="17" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M9 2.2l7 12.3H2L9 2.2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M9 7v3.2M9 12.3v.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IconLink = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M6 8a2.5 2.5 0 003.5 0l2-2A2.5 2.5 0 008 2.5l-1 1M8 6a2.5 2.5 0 00-3.5 0l-2 2A2.5 2.5 0 006 11.5l1-1"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

/** Dominio legible: el usuario reconoce "landos.com", no la URL completa. */
function prettyDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
}

function scoreColor(percent: number, theme: { success: string; warning: string; error: string }): string {
  if (percent >= 80) return theme.success;
  if (percent >= 50) return theme.warning;
  return theme.error;
}

export function WorkspaceHubPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { workspaces, isLoading } = useWorkspaces();
  const fromList = workspaces.find((w) => w.id === workspaceId);

  // Si el workspace no está en la lista (ej: un admin viendo uno ajeno desde el
  // panel), lo traemos por id. Antes quedaba en skeleton para siempre.
  const [fetched, setFetched] = useState<Workspace | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [brand, setBrand] = useState<BrandInfo | null>(null);
  const [posts, setPosts] = useState<GeneratedPost[]>([]);
  const [genOpen, setGenOpen] = useState(false);

  useEffect(() => {
    setFetched(null);
    setNotFound(false);
    if (isLoading || fromList || !workspaceId) return;
    apiClient.get<Workspace>(`/api/workspaces/${workspaceId}`)
      .then(setFetched)
      .catch(() => setNotFound(true));
  }, [workspaceId, fromList, isLoading]);

  useEffect(() => {
    if (!workspaceId) return;
    setBrand(null);
    setPosts([]);
    // Los dos son opcionales: una marca recién creada no tiene ni brand-info ni
    // posts, y eso no es un error — es el estado inicial.
    apiClient.get<BrandInfo | null>(`/api/workspaces/${workspaceId}/brand-info`)
      .then((b) => setBrand(b ?? null))
      .catch(() => setBrand(null));
    apiClient.get<GeneratedPost[]>(`/api/workspaces/${workspaceId}/posts?limit=${RECENT_POSTS_LIMIT}`)
      .then((p) => setPosts(Array.isArray(p) ? p : []))
      .catch(() => setPosts([]));
  }, [workspaceId]);

  const workspace = fromList || fetched;

  const checklist = useMemo(
    () => buildChecklist({ logoUrl: workspace?.logoUrl, assetsCount: workspace?.assetsCount }, brand),
    [workspace?.logoUrl, workspace?.assetsCount, brand]
  );

  if (notFound) {
    return <NotFoundBox>Workspace not found or you don&apos;t have access to it.</NotFoundBox>;
  }
  if (!workspace) {
    return <PageSkeleton />;
  }

  const go = (path: string) => navigate(`/workspace/${workspace.id}/${path}`);
  const overview = brand?.businessOverview || brand?.description || '';
  const website = brand?.sourceUrl || '';
  const lastPost = posts[0];
  const lastDate = lastPost ? new Date(lastPost.createdAt).toLocaleDateString('en-US') : null;
  const colors = (brand?.brandColors || []).slice(0, 8);
  const topics = (brand?.postTopics || []).slice(0, 6);
  const missing = checklist.missingRequired;

  return (
    <HubLayout>
      <BrandHeader>
        <BrandIdentity>
          {workspace.logoUrl ? (
            <BrandLogoTile>
              <img src={workspace.logoUrl} alt="" />
            </BrandLogoTile>
          ) : (
            <BrandLogoFallback $gradient={gradientFor(workspace.id)}>
              {workspace.name.charAt(0).toUpperCase()}
            </BrandLogoFallback>
          )}

          <BrandTitleBlock>
            <BrandName>{workspace.name}</BrandName>
            <BrandMetaRow>
              {website ? (
                <WebsiteLink href={website} target="_blank" rel="noopener noreferrer">
                  <IconLink /> {prettyDomain(website)}
                </WebsiteLink>
              ) : (
                <CardLink type="button" onClick={() => go('brand?tab=sources')}>+ Add website</CardLink>
              )}
              <MetaDot>·</MetaDot>
              <span>{workspace.postsCount || 0} posts</span>
              {lastDate && (<><MetaDot>·</MetaDot><span>Last batch {lastDate}</span></>)}
            </BrandMetaRow>
          </BrandTitleBlock>
        </BrandIdentity>

        <HeaderActions>
          <ActionButton $primary type="button" onClick={() => setGenOpen(true)}>
            <IconSpark /> Generate
          </ActionButton>
          <ActionButton type="button" onClick={() => go('brand')}>Brand</ActionButton>
          <ActionButton type="button" onClick={() => go('templates')}>Templates</ActionButton>
          <ActionButton type="button" onClick={() => go('gallery')}>Gallery</ActionButton>
        </HeaderActions>
      </BrandHeader>

      {missing.length > 0 && (
        <Alert>
          <AlertIcon><IconWarn /></AlertIcon>
          <AlertBody>
            <strong>
              {missing.length} required {missing.length === 1 ? 'field is' : 'fields are'} missing:{' '}
              {missing.map((m) => m.label).join(', ')}.
            </strong>
            <p>{missing[0].hint}</p>
            <AlertFix type="button" onClick={() => go(missing[0].fixPath)}>
              Complete {missing.length === 1 ? missing[0].label.toLowerCase() : 'brand setup'} →
            </AlertFix>
          </AlertBody>
        </Alert>
      )}

      <HubGrid>
        <Column>
          <Card $delay={60}>
            <CardHead>
              <CardTitle>What this brand does</CardTitle>
              <CardLink type="button" onClick={() => go('brand')}>Edit</CardLink>
            </CardHead>
            {overview ? (
              <>
                <Overview>{overview}</Overview>
                {topics.length > 0 && (
                  <TagRow>{topics.map((t) => <Tag key={t}>{t}</Tag>)}</TagRow>
                )}
              </>
            ) : (
              <EmptyHint>
                No description yet. Every post is written from this — without it the copy
                comes out generic. Paste the website on the Brand page and let Novah fill it in.
              </EmptyHint>
            )}
          </Card>

          <Card $delay={120}>
            <CardHead>
              <CardTitle>Recent posts</CardTitle>
              {posts.length > 0 && (
                <CardLink type="button" onClick={() => go('generate')}>View all</CardLink>
              )}
            </CardHead>
            {posts.length > 0 ? (
              <PostsGrid>
                {posts.map((p, i) => (
                  <PostCell key={p.id} $i={i}>
                    <PostThumb
                      type="button"
                      title={p.content?.slice(0, 120)}
                      aria-label={`Open post for ${p.network}`}
                      onClick={() => navigate(`/workspace/${workspace.id}/posts/${p.id}/edit`)}
                    >
                      {p.imageUrl
                        ? <img src={p.imageUrl} alt="" loading="lazy" />
                        : <PostTextFallback>{p.content}</PostTextFallback>}
                      <PostNetwork>{p.network}</PostNetwork>
                    </PostThumb>
                  </PostCell>
                ))}
              </PostsGrid>
            ) : (
              <EmptyHint>No posts yet. Hit Generate and Novah writes the first batch.</EmptyHint>
            )}
          </Card>
        </Column>

        <Column>
          <Card $delay={90}>
            <CardHead>
              <CardTitle>Brand completeness</CardTitle>
            </CardHead>
            <ScoreRow>
              <ScoreRingWrap>
                <ScoreRing
                  $percent={checklist.percent}
                  $color={scoreColor(checklist.percent, {
                    success: '#10B981', warning: '#F59E0B', error: '#EF4444',
                  })}
                />
                <ScoreValue>{checklist.percent}%</ScoreValue>
              </ScoreRingWrap>
              <ScoreText>
                <strong>
                  {checklist.percent >= 80 ? 'Ready to generate'
                    : checklist.percent >= 50 ? 'Good, but thin'
                      : 'Needs more info'}
                </strong>
                <span>
                  The more Novah knows about the brand, the less generic the posts come out.
                </span>
              </ScoreText>
            </ScoreRow>

            <CheckList>
              {checklist.items.map((item) => (
                <CheckRow key={item.key}>
                  <CheckAction
                    type="button"
                    $done={item.done}
                    title={`${item.hint} — click to edit`}
                    aria-label={`${item.label}: ${item.done ? 'complete' : 'missing'}. Edit`}
                    onClick={() => go(item.fixPath)}
                  >
                    <CheckMark $done={item.done} $required={item.required}>
                      {item.done ? <IconCheck /> : <IconDash />}
                    </CheckMark>
                    <CheckLabel>{item.label}</CheckLabel>
                    {!item.done && item.required && <RequiredBadge>Required</RequiredBadge>}
                    <CheckGo className="go" aria-hidden="true">→</CheckGo>
                  </CheckAction>
                </CheckRow>
              ))}
            </CheckList>
          </Card>

          <Card $delay={150}>
            <CardHead><CardTitle>At a glance</CardTitle></CardHead>
            <StatsRow>
              <Stat>
                <strong>{workspace.postsCount || 0}</strong>
                <span>posts</span>
              </Stat>
              <Stat>
                <strong>{workspace.assetsCount || 0}</strong>
                <span>assets</span>
              </Stat>
              <Stat>
                <strong>{checklist.items.filter((i) => i.done).length}/{checklist.items.length}</strong>
                <span>brand data</span>
              </Stat>
            </StatsRow>

            {colors.length > 0 && (
              <>
                <CardHead style={{ marginTop: 18, marginBottom: 10 }}>
                  <CardTitle>Brand colors</CardTitle>
                </CardHead>
                <Swatches>
                  {colors.map((c) => <Swatch key={c} $color={c} title={c} />)}
                </Swatches>
              </>
            )}
          </Card>
        </Column>
      </HubGrid>

      <GenerateOptionsModal
        workspaceId={workspace.id}
        open={genOpen}
        onClose={() => setGenOpen(false)}
      />
    </HubLayout>
  );
}
